import { motion } from 'framer-motion';
import PageError from '../components/PageError';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  User,
  Phone,
  Mail,
  Bus,
  BusFront,
  Star,
  AlertCircle,
  RefreshCw,
  Filter,
  X,
  Route,
  AlertTriangle,
  MoreHorizontal,
  Download,
} from 'lucide-react';
import { exportToCsv } from '../utils/exportCsv';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdownMenu';
import DriverAssignmentModal from '../components/DriverAssignmentModal';
import DriverFormModal from '../components/DriverFormModal';
import FeatureGate from '../components/FeatureGate';
import DeleteImpactModal from '../components/DeleteImpactModal';
import useDrivers, { getStatusColor, getStatusLabel } from '../hooks/trDrivers';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { useSortPaginate } from '../hooks/useSortPaginate';
import { SortableHeader, TablePagination, ColumnToggle, useColumnVisibility } from '../components/TableControls';
import { StatusBadge } from '../components/ui/StatusBadge';

const DRIVER_COLUMNS = [
  { key: 'contact', label: 'Contact' },
  { key: 'assignation', label: 'Assignation' },
  { key: 'status', label: 'Statut' },
];

const DriverManagement = () => {
  const { visibility, toggle, visible } = useColumnVisibility({ contact: true, assignation: true, status: true });
  const {
    filteredDrivers,
    loading,
    error,
    stats,
    filters,
    assignmentModalOpen,
    selectedDriver,
    formModalOpen,
    formMode,
    driverToEdit,
    formLoading,
    deleteModalOpen,
    driverToDelete,
    updateFilters,
    loadDrivers,
    handleCreateDriver,
    handleEditDriver,
    handleDeleteDriver,
    confirmDeleteDriver,
    setDeleteModalOpen,
    handleSubmitDriver,
    handleCloseFormModal,
    handleCloseAssignmentModal,
    handleAssignmentSubmit,
  } = useDrivers();

  const { paginated, page, totalPages, totalItems, pageSize, sortCol, sortDir, setPage, toggleSort } = useSortPaginate(
    filteredDrivers,
    { col: 'last_name|lastName', dir: 'asc' },
    15,
    [filters],
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Chauffeurs</h1>
          <p className="text-gray-600">Gérez votre équipe de chauffeurs</p>
        </div>
        <div className="hidden md:flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={() => {
              const isoToDMY = (d) => (d ? d.replace(/^(\d{4})-(\d{2})-(\d{2}).*/, '$3/$2/$1') : '');
              exportToCsv(
                filteredDrivers,
                [
                  { label: 'Nom', value: (d) => d.last_name || d.lastName || '' },
                  { label: 'Prénom', value: (d) => d.first_name || d.firstName || '' },
                  { label: 'Email', value: (d) => d.email || '' },
                  { label: 'Téléphone', value: (d) => d.phone || '' },
                  { label: 'N° Permis', value: (d) => d.license_number || '' },
                  { label: 'Exp. Permis', value: (d) => isoToDMY(d.license_expiry) },
                  { label: 'Date Embauche', value: (d) => isoToDMY(d.hire_date) },
                  { label: 'Note', value: (d) => (d.rating != null ? d.rating : '') },
                  { label: 'Statut', value: (d) => d.status || '' },
                  { label: 'Bus', value: (d) => d.assigned_bus?.bus_number || '' },
                  { label: 'Circuit', value: (d) => d.assigned_route?.route_name || '' },
                ],
                'chauffeurs.csv',
              );
            }}
          >
            <Download className="w-4 h-4" />
            Exporter CSV
          </Button>
          <Button onClick={handleCreateDriver}>
            <Plus className="w-4 h-4" />
            Nouveau Chauffeur
          </Button>
        </div>
      </div>

      {/* Statistiques */}
      {!loading && !error && (
        <FeatureGate page="drivers" feature="metrics">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                key: 'total',
                label: 'Total',
                value: stats?.total || 0,
                icon: User,
                color: 'blue',
                textColor: 'text-gray-900',
              },
              {
                key: 'active',
                label: 'Actifs',
                value: stats?.active || 0,
                icon: User,
                color: 'green',
                textColor: 'text-green-600',
              },
              {
                key: 'assigned',
                label: 'Assignés',
                value: stats?.assigned || 0,
                icon: Bus,
                color: 'blue',
                textColor: 'text-blue-600',
              },
              {
                key: 'averageRating',
                label: 'Note Moyenne',
                value: stats?.averageRating || 0,
                icon: Star,
                color: 'yellow',
                textColor: 'text-yellow-600',
              },
              {
                key: 'expiredLicenses',
                label: 'Permis Expirant',
                value: stats?.expiredLicenses || 0,
                icon: AlertCircle,
                color: 'red',
                textColor: 'text-red-600',
              },
            ].map(({ key, label, value, icon: Icon, color, textColor }, index) => (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{label}</p>
                    <p className={`text-2xl font-bold ${textColor}`}>{value}</p>
                  </div>
                  <div className={`w-12 h-12 bg-${color}-100 rounded-lg flex items-center justify-center`}>
                    <Icon className={`w-6 h-6 text-${color}-600`} />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </FeatureGate>
      )}

      {/* Barre de filtres */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Rechercher par nom, prénom ou email"
              value={filters?.search || ''}
              onChange={(e) => updateFilters({ search: e.target.value })}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
            {filters?.search && (
              <button
                onClick={() => updateFilters({ search: '' })}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filtres:</span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {[
                { value: '', label: 'Tous', active: 'bg-blue-100 text-blue-800' },
                { value: 'active', label: 'Actifs', active: 'bg-green-100 text-green-800' },
                { value: 'on-leave', label: 'En congé', active: 'bg-yellow-100 text-yellow-800' },
                { value: 'inactive', label: 'Inactifs', active: 'bg-gray-100 text-gray-800' },
              ].map(({ value, label, active }) => (
                <button
                  key={label}
                  onClick={() => updateFilters({ status: value })}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    filters?.status === value ? active : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              onClick={() => loadDrivers()}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Actualiser"
            >
              <RefreshCw size={14} />
            </button>
            <ColumnToggle columns={DRIVER_COLUMNS} visibility={visibility} onToggle={toggle} />
          </div>
        </div>
      </div>

      {/* Tableau / Cards — chauffeurs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {!loading && error && <PageError error={error} onRetry={loadDrivers} variant="inline" />}
        {!error && (
          <>
            {/* ── Vue cartes (mobile < md) ── */}
            <div className="md:hidden divide-y divide-gray-100">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <div key={`skel-m-${i}`} className="p-4 flex items-start gap-3">
                    <Skeleton className="h-10 w-10 rounded-full shrink-0" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-36" />
                      <Skeleton className="h-3 w-28" />
                      <Skeleton className="h-3 w-32" />
                    </div>
                  </div>
                ))
              ) : filteredDrivers.length === 0 ? (
                <div className="p-8 text-center">
                  <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun chauffeur trouvé</h3>
                  <p className="text-gray-600 mb-4">
                    {filters?.search || filters?.status
                      ? 'Aucun chauffeur ne correspond aux critères de recherche.'
                      : 'Commencez par ajouter votre premier chauffeur.'}
                  </p>
                  <Button onClick={handleCreateDriver}>
                    <Plus className="w-4 h-4" />
                    Nouveau Chauffeur
                  </Button>
                </div>
              ) : (
                paginated.map((driver, index) => {
                  if (!driver) return null;
                  return (
                    <motion.div
                      key={driver?.id || index}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="p-4 hover:bg-gray-50"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                            <BusFront size={20} className="text-blue-600" />
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-gray-900 truncate">
                              {driver?.first_name || driver?.firstName || 'N/A'}{' '}
                              {driver?.last_name || driver?.lastName || 'N/A'}
                            </div>
                            {driver?.ref && <div className="text-xs text-gray-400 truncate">{driver.ref}</div>}
                          </div>
                        </div>
                        <StatusBadge
                          status={driver?.status}
                          label={getStatusLabel(driver?.status)}
                          className={getStatusColor(driver?.status)}
                        />
                      </div>
                      <div className="mt-2.5 ml-[52px] space-y-1.5">
                        <div className="flex items-center gap-1.5">
                          <Phone size={13} className="text-gray-400 shrink-0" />
                          <span className="text-sm text-gray-600">{driver?.phone || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Mail size={13} className="text-gray-400 shrink-0" />
                          <span className="text-sm text-gray-600 truncate max-w-[220px]">{driver?.email || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {driver?.assigned_bus?.bus_number ? (
                            <>
                              <Bus size={13} className="text-gray-400 shrink-0" />
                              <span className="text-sm text-gray-600">Bus {driver.assigned_bus.bus_number}</span>
                            </>
                          ) : (
                            <>
                              <AlertTriangle size={13} className="text-orange-400 shrink-0" />
                              <span className="text-sm text-gray-400">Pas de bus</span>
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5">
                          {driver?.assigned_route?.route_name ? (
                            <>
                              <Route size={13} className="text-gray-400 shrink-0" />
                              <span className="text-sm text-gray-600 truncate max-w-[200px]">
                                {driver.assigned_route.route_name}
                              </span>
                            </>
                          ) : (
                            <>
                              <AlertTriangle size={13} className="text-orange-400 shrink-0" />
                              <span className="text-sm text-gray-400">Pas de circuit</span>
                            </>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </div>

            {/* ── Tableau (desktop ≥ md) ── */}
            {loading ? (
              <div className="overflow-x-auto hidden md:block">
                <table className="min-w-[640px] divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Chauffeur', 'Contact', 'Assignation'].map((h) => (
                        <th
                          key={h}
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          {h}
                        </th>
                      ))}
                      <th className="px-4 py-3 w-[1%] whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-4 py-3 w-[1%] whitespace-nowrap text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i}>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <Skeleton className="w-10 h-10 rounded-full" />
                            <div className="space-y-1.5">
                              <Skeleton className="h-3.5 w-28" />
                              <Skeleton className="h-3 w-20" />
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="space-y-1.5">
                            <Skeleton className="h-3.5 w-32" />
                            <Skeleton className="h-3 w-24" />
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="space-y-1.5">
                            <Skeleton className="h-3.5 w-24" />
                            <Skeleton className="h-3 w-20" />
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <Skeleton className="h-5 w-16 rounded-full" />
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex justify-end">
                            <Skeleton className="h-7 w-7 rounded" />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : filteredDrivers.length > 0 ? (
              <>
                <div className="overflow-x-auto hidden md:block">
                  <table className="min-w-[640px] divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <SortableHeader
                          col="last_name|lastName"
                          sortCol={sortCol}
                          sortDir={sortDir}
                          onSort={toggleSort}
                        >
                          Chauffeur
                        </SortableHeader>
                        {visible('contact') && (
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Contact
                          </th>
                        )}
                        {visible('assignation') && (
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Assignation
                          </th>
                        )}
                        {visible('status') && (
                          <SortableHeader
                            col="status"
                            sortCol={sortCol}
                            sortDir={sortDir}
                            onSort={toggleSort}
                            className="w-[1%] whitespace-nowrap"
                          >
                            Statut
                          </SortableHeader>
                        )}
                        <th className="px-4 py-3 w-[1%] whitespace-nowrap text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {paginated.map((driver, index) => {
                        if (!driver) return null;
                        return (
                          <tr key={driver?.id || index} className="hover:bg-gray-50">
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                                  <BusFront size={20} className="text-blue-600" />
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">
                                    {driver?.first_name || driver?.firstName || 'N/A'}{' '}
                                    {driver?.last_name || driver?.lastName || 'N/A'}
                                  </div>
                                  {driver?.ref && <div className="text-sm text-gray-400">{driver.ref}</div>}
                                </div>
                              </div>
                            </td>
                            {visible('contact') && (
                              <td className="px-4 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  <div className="flex items-center mb-1">
                                    <Phone size={14} className="text-gray-400 mr-2" />
                                    {driver?.phone || 'N/A'}
                                  </div>
                                  <div className="flex items-center">
                                    <Mail size={14} className="text-gray-400 mr-2" />
                                    {driver?.email || 'N/A'}
                                  </div>
                                </div>
                              </td>
                            )}
                            {visible('assignation') && (
                              <td className="px-4 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900 flex items-center gap-2">
                                  {driver?.assigned_bus?.bus_number != null && driver.assigned_bus.bus_number !== '' ? (
                                    <>
                                      <Bus size={14} className="text-gray-400 shrink-0" />
                                      <span>{driver.assigned_bus.bus_number}</span>
                                    </>
                                  ) : (
                                    <>
                                      <AlertTriangle size={14} className="text-orange-500 shrink-0" />
                                      <span>Pas de bus assigné</span>
                                    </>
                                  )}
                                </div>
                                <div className="text-sm text-gray-900 flex items-center gap-2 mt-1">
                                  {driver?.assigned_route?.route_name != null &&
                                  driver.assigned_route.route_name !== '' ? (
                                    <>
                                      <Route size={14} className="text-gray-400 shrink-0" />
                                      <span>{driver.assigned_route.route_name}</span>
                                    </>
                                  ) : (
                                    <>
                                      <AlertTriangle size={14} className="text-orange-500 shrink-0" />
                                      <span>Pas de circuit assigné</span>
                                    </>
                                  )}
                                </div>
                              </td>
                            )}
                            {visible('status') && (
                              <td className="px-4 py-4 whitespace-nowrap">
                                <StatusBadge
                                  status={driver?.status}
                                  label={getStatusLabel(driver?.status)}
                                  className={getStatusColor(driver?.status)}
                                />
                              </td>
                            )}
                            <td className="px-4 py-4 whitespace-nowrap text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button
                                    aria-label="Actions"
                                    className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                                  >
                                    <MoreHorizontal size={16} />
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-40">
                                  <DropdownMenuItem onClick={() => handleEditDriver(driver)}>
                                    <Edit size={14} className="mr-2" />
                                    Modifier
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => handleDeleteDriver(driver)}
                                    className="text-red-600 focus:text-red-600 focus:bg-red-50"
                                  >
                                    <Trash2 size={14} className="mr-2" />
                                    Supprimer
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <TablePagination
                  page={page}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  pageSize={pageSize}
                  onPageChange={setPage}
                />
              </>
            ) : (
              <div className="p-8 text-center hidden md:block">
                <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun chauffeur trouvé</h3>
                <p className="text-gray-600 mb-4">
                  {filters?.search || filters?.status
                    ? 'Aucun chauffeur ne correspond aux critères de recherche.'
                    : 'Commencez par ajouter votre premier chauffeur.'}
                </p>
                <Button onClick={handleCreateDriver}>
                  <Plus className="w-4 h-4" />
                  Nouveau Chauffeur
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {formModalOpen && (
        <DriverFormModal
          isOpen={formModalOpen}
          onClose={handleCloseFormModal}
          onSubmit={handleSubmitDriver}
          driver={driverToEdit}
          mode={formMode}
          loading={formLoading}
        />
      )}

      {assignmentModalOpen && (
        <DriverAssignmentModal
          isOpen={assignmentModalOpen}
          onClose={handleCloseAssignmentModal}
          onSubmit={handleAssignmentSubmit}
          driver={selectedDriver}
        />
      )}

      {deleteModalOpen && driverToDelete && (
        <DeleteImpactModal
          entity={driverToDelete}
          entityType="driver"
          entityName={`${driverToDelete.first_name || driverToDelete.firstName || ''} ${driverToDelete.last_name || driverToDelete.lastName || ''}`.trim()}
          onConfirm={confirmDeleteDriver}
          onCancel={() => setDeleteModalOpen(false)}
        />
      )}
    </div>
  );
};

export default DriverManagement;
